import React, { useState, useEffect, useCallback } from "react";
import classnames from "classnames";

import Analytics from "analytics/util/Analytics";
import Voucher from "features/vouchers";
import OkLoader from "shared/components/OkLoader";
import Modal from "shared/components/Modal";
import Context from "shared/util/Context";

import EnterPromoCodeModal from "shared/components/VoucherModal/EnterPromoCode";
import SignupOrLoginModal from "shared/components/VoucherModal/SignupOrLogin";
import Results from "shared/components/VoucherModal/Results";
import {
	processVoucher,
	saveVoucher,
	hasSavedVoucher,
	getVoucherCodeFromUrl,
} from "shared/components/VoucherModal/helpers";
import {
	VoucherStatus,
} from "shared/components/VoucherModal/constants";
import "shared/components/VoucherModal/VoucherModal.scss";

function VoucherModal() {
	const [voucherCode, setVoucherCode] = useState(getVoucherCodeFromUrl() || "");
	const [hasSubmittedCode, setHasSubmittedCode] = useState(false);
	const [voucherStatus, setVoucherStatus] = useState(null);
	const [isLoading, setIsLoading] = useState(true);
	const isLoggedIn = !!Context.get("userid");

	// Exit the modal.
	const onCloseModal = () => {
		Voucher.close();

		if (voucherStatus === VoucherStatus.SUCCESS) {
			window.location.reload();
		}
	};

	// Process a voucher.
	const handleVoucherStatus = (voucher) => {
		return processVoucher(voucher)
			.then((res) => {
				let response = VoucherStatus.FAILURE;

				if (res.redeemed_product.productid.length > 0) {
					response = VoucherStatus.SUCCESS;
				}

				if (response === VoucherStatus.SUCCESS) {
					Analytics.logUIEvent(Analytics.eventNames.voucherViewedSuccess, {
						voucher,
					});
				} else if (response === VoucherStatus.FAILURE) {
					Analytics.logUIEvent(Analytics.eventNames.voucherViewedError, {
						voucher,
					});
				}

				setVoucherStatus(response);
			});
	};

	const handleVoucherCode = useCallback((code) => {
		setIsLoading(true);
		saveVoucher(code);
		setHasSubmittedCode(true);

		if (isLoggedIn) {
			handleVoucherStatus(code).finally(() => {
				setIsLoading(false);
			});
		} else {
			setIsLoading(false);
		}
	}, [isLoggedIn]);

	// Submit a voucher via the form.
	const onPromoCodeSubmit = () => {
		Analytics.logUIEvent(Analytics.eventNames.voucherSubmitCode, {
			data: {
				voucher: voucherCode,
			},
		});
		handleVoucherCode(voucherCode);
	};

	// Change voucher value.
	const onPromoCodeChange = (event) => {
		const promoCode = event.target.value.toUpperCase();
		setVoucherCode(promoCode);
	};

	const onRetryVoucher = () => {
		setHasSubmittedCode(false);
		setVoucherStatus(null);
		setVoucherCode("");
	};

	const onSignup = () => {
		Analytics.logUIEvent(Analytics.eventNames.voucherSelectedCreateAccount, {
			data: {
				voucher: voucherCode,
			},
		});
	};

	const onLogin = () => {
		Analytics.logUIEvent(Analytics.eventNames.voucherSelectedSignIn, {
			data: {
				voucher: voucherCode,
			},
		});
	};

	// Handle any previously stored vouchers.
	useEffect(() => {
		const storedVoucher = hasSavedVoucher();

		if (!storedVoucher) {
			setIsLoading(false);

			return;
		}

		handleVoucherCode(storedVoucher);
	}, [handleVoucherCode]);

	// Voucher app states.
	const showEnterPromoCode = !hasSubmittedCode && !voucherStatus && !isLoading;
	const showSignUpOrLogin = hasSubmittedCode && !isLoggedIn && !isLoading;
	const showResults = !!voucherStatus && !isLoading;
	const showLoading = isLoading;
	const className = classnames("voucher-modal", {
		"voucher-modal--enter-promo-code": showEnterPromoCode,
		"voucher-modal--signup-or-login": showSignUpOrLogin,
		"voucher-modal--results": showResults,
		"voucher-modal--loading": showLoading,
	});

	return (
		<Modal
			id="voucherModal"
			className={className}
			onCancel={onCloseModal}
			show
		>
			{showLoading && (
				<OkLoader show />
			)}
			{showEnterPromoCode && (
				<EnterPromoCodeModal
					promoCode={voucherCode}
					onPromoCodeChange={onPromoCodeChange}
					onPromoCodeSubmit={onPromoCodeSubmit}
				/>
			)}
			{showSignUpOrLogin && (
				<SignupOrLoginModal
					onSignup={onSignup}
					onLogin={onLogin}
				/>
			)}
			{showResults && (
				<Results
					voucherStatus={voucherStatus}
					closeModal={onCloseModal}
					onRetryVoucher={onRetryVoucher}
				/>
			)}
		</Modal>
	);
}

export default VoucherModal;




// WEBPACK FOOTER //
// ./src/shared/components/VoucherModal/index.jsx